import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ArrowsAngleContract,
  ArrowsFullscreen,
  Stars,
  PencilSquare,
  Send,
  X,
} from 'react-bootstrap-icons';
import {
  AgentAction,
  AgentChatMessage,
  formatAgentTimings,
  sendAgentChat,
} from '../../api/aiAgent';
import { isIntelligenceBuildEnabled } from '../../config/intelligence';
import {
  creditsRemainingOf,
  useIntelligenceProductStatus,
} from '../../hooks/useIntelligenceProductStatus';
import {
  canWritePanelArea,
  PANEL_PERMISSIONS,
} from '../../utils/panelPermissions';
import {
  isRenderablePendingAction,
  renderActionCard,
} from './actionRegistry';
import ChatMarkdown from './ChatMarkdown';
import IntelligenceActivateOffer from './hire/IntelligenceActivateOffer';
import { Organization } from '../../models';
import styles from './IntelligenceChatShell.module.scss';

export type ChatShellMode = 'collapsed' | 'docked' | 'fullscreen';

type ChatItem =
  | {
      kind: 'text';
      role: 'user' | 'model';
      content: string;
      timings?: Record<string, number> | null;
    }
  | { kind: 'action'; action: AgentAction; resolved?: boolean };

interface IntelligenceChatShellProps {
  organizationId: string | null | undefined;
  organization?: Organization | null;
  panelPermissions?: string[] | null;
  isGodMode?: boolean;
}

const STORAGE_KEY = 'mayorx.intelligenceChat.mode';

const normalizeMode = (raw: string | null): ChatShellMode => {
  if (raw === 'docked' || raw === 'collapsed' || raw === 'fullscreen') return raw;
  // Legacy "expanded" side panel → immersive fullscreen
  if (raw === 'expanded') return 'fullscreen';
  return 'collapsed';
};

const IntelligenceChatShell: React.FC<IntelligenceChatShellProps> = ({
  organizationId,
  organization = null,
  panelPermissions = null,
  isGodMode = false,
}) => {
  const [mode, setMode] = useState<ChatShellMode>(() => {
    try {
      return normalizeMode(localStorage.getItem(STORAGE_KEY));
    } catch {
      return 'collapsed';
    }
  });
  const { status, reload: reloadStatus, applyCredits } =
    useIntelligenceProductStatus(organizationId);
  const [items, setItems] = useState<ChatItem[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const canWriteOrders = canWritePanelArea(
    panelPermissions,
    PANEL_PERMISSIONS.ORDERS,
    isGodMode
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [items, mode, sending]);

  const productLive = Boolean(status?.productEnabled);
  const showShell =
    Boolean(organizationId) &&
    canWriteOrders &&
    (isIntelligenceBuildEnabled() || productLive) &&
    productLive;

  const hasCapacity = Boolean(status?.capacityId);
  const creditsLeft = creditsRemainingOf(status);
  const outOfCredits = hasCapacity && creditsLeft !== null && creditsLeft <= 0;
  const trialing = Boolean(status?.trialing);
  const hireVariant = !hasCapacity
    ? 'activate'
    : trialing
      ? 'trialExhausted'
      : 'outOfCredits';
  const showHireOffer = Boolean(
    organizationId && status && (!hasCapacity || outOfCredits)
  );

  const persistMode = useCallback((next: ChatShellMode) => {
    setMode(next);
  }, []);

  useEffect(() => {
    if (mode !== 'fullscreen') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') persistMode('docked');
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mode, persistMode]);

  const handleNewChat = useCallback(() => {
    if (sending) return;
    setItems([]);
    setDraft('');
    setError(null);
  }, [sending]);

  const historyFromItems = useCallback((): AgentChatMessage[] => {
    return items
      .filter((item): item is Extract<ChatItem, { kind: 'text' }> => item.kind === 'text')
      .map(item => ({ role: item.role, content: item.content }));
  }, [items]);

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text || !organizationId || sending || !hasCapacity || outOfCredits) {
      return;
    }
    setError(null);
    setDraft('');
    const history = historyFromItems();
    setItems(prev => [...prev, { kind: 'text', role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await sendAgentChat({
        organizationId,
        message: text,
        history,
      });
      const next: ChatItem[] = [
        {
          kind: 'text',
          role: 'model',
          content: res.reply,
          timings: res.timings,
        },
      ];
      for (const action of res.actions || []) {
        if (isRenderablePendingAction(action)) {
          next.push({ kind: 'action', action });
        }
      }
      setItems(prev => [...prev, ...next]);
      applyCredits(res.creditsRemaining, res.creditsCharged);
    } catch (e: any) {
      if (e?.response?.data?.detail?.code === 'intelligence_quota_exceeded') {
        applyCredits(0, 1);
        setError(null);
        void reloadStatus();
      } else {
        const detail =
          e?.response?.data?.detail?.message ||
          e?.response?.data?.detail ||
          e?.message ||
          'No se pudo completar la solicitud';
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      setItems(prev => prev.slice(0, -1));
      setDraft(text);
    } finally {
      setSending(false);
    }
  }, [
    draft,
    organizationId,
    sending,
    hasCapacity,
    outOfCredits,
    historyFromItems,
    applyCredits,
    reloadStatus,
  ]);

  const handleConfirmAction = useCallback(
    async (action: AgentAction, actionIndex: number) => {
      if (!organizationId || sending || !hasCapacity || outOfCredits) return;
      setError(null);
      setSending(true);
      try {
        const res = await sendAgentChat({
          organizationId,
          message: '',
          history: historyFromItems(),
          confirmAction: action,
        });
        setItems(prev => {
          const copy = [...prev];
          const current = copy[actionIndex];
          if (current?.kind === 'action') {
            copy[actionIndex] = { ...current, resolved: true };
          }
          copy.push({
            kind: 'text',
            role: 'model',
            content: res.reply,
            timings: res.timings,
          });
          return copy;
        });
        applyCredits(res.creditsRemaining, res.creditsCharged);
      } catch (e: any) {
        if (e?.response?.data?.detail?.code === 'intelligence_quota_exceeded') {
          applyCredits(0, 1);
          setError(null);
          void reloadStatus();
        } else {
          const detail =
            e?.response?.data?.detail?.message ||
            e?.response?.data?.detail ||
            e?.message ||
            'No se pudo confirmar la acción';
          setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }
      } finally {
        setSending(false);
      }
    },
    [
      organizationId,
      sending,
      hasCapacity,
      outOfCredits,
      historyFromItems,
      applyCredits,
      reloadStatus,
    ]
  );

  const handleDismissAction = useCallback((actionIndex: number) => {
    setItems(prev => {
      const copy = [...prev];
      const current = copy[actionIndex];
      if (current?.kind === 'action') {
        copy[actionIndex] = { ...current, resolved: true };
      }
      copy.push({
        kind: 'text',
        role: 'model',
        content: 'Borrador descartado. Dime qué quieres cambiar.',
      });
      return copy;
    });
  }, []);

  if (!showShell) {
    return null;
  }

  if (mode === 'collapsed') {
    return (
      <div className={styles.shell}>
        <button
          type="button"
          className={styles.collapsedTrigger}
          aria-label="Abrir Intelligence"
          title="Intelligence"
          onClick={() => persistMode('docked')}
        >
          <Stars size={18} className={styles.collapsedIcon} aria-hidden />
          <span className={styles.collapsedLabel}>Intelligence</span>
        </button>
      </div>
    );
  }

  const isFullscreen = mode === 'fullscreen';
  const panelClass = isFullscreen
    ? `${styles.panel} ${styles.panelFullscreen}`
    : `${styles.panel} ${styles.panelDocked}`;

  return (
    <div
      className={`${styles.shell} ${panelClass}`}
      role={isFullscreen ? 'dialog' : 'complementary'}
      aria-modal={isFullscreen || undefined}
      aria-label="MayorX Intelligence"
    >
      <div className={styles.stage}>
        <div className={styles.header}>
          <div className={styles.titleBlock}>
            <h2 className={styles.title}>Intelligence</h2>
            <p className={styles.subtitle}>
              {!hasCapacity
                ? status?.trialAvailable
                  ? 'Prueba Plus 14 días o activa Lite, Plus o Extra'
                  : 'Activa Lite, Plus o Extra para chatear'
                : outOfCredits && trialing
                  ? 'Se acabaron los créditos de la prueba'
                  : outOfCredits
                    ? 'Sin créditos — compra un paquete abajo'
                    : creditsLeft !== null
                      ? `${creditsLeft} créditos restantes`
                      : 'Asistente de ventas'}
            </p>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.newChatBtn}
              aria-label="Nuevo chat"
              title="Nuevo chat"
              disabled={sending || (items.length === 0 && !draft && !error)}
              onClick={handleNewChat}
            >
              <PencilSquare size={14} aria-hidden />
              <span>Nuevo</span>
            </button>
            {isFullscreen ? (
              <button
                type="button"
                className={styles.iconBtn}
                aria-label="Salir de pantalla completa"
                title="Salir de pantalla completa (Esc)"
                onClick={() => persistMode('docked')}
              >
                <ArrowsAngleContract size={16} />
              </button>
            ) : (
              <button
                type="button"
                className={styles.iconBtn}
                aria-label="Pantalla completa"
                title="Pantalla completa"
                onClick={() => persistMode('fullscreen')}
              >
                <ArrowsFullscreen size={16} />
              </button>
            )}
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Cerrar"
              onClick={() => persistMode('collapsed')}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.messages}>
          {showHireOffer && organizationId && status && (
            <IntelligenceActivateOffer
              organizationId={organizationId}
              organization={organization}
              status={status}
              onActivated={() => {
                void reloadStatus();
              }}
              variant={hireVariant}
            />
          )}
          {hasCapacity && !outOfCredits && items.length === 0 && !sending && (
            <div className={isFullscreen ? styles.emptyHero : styles.empty}>
              {isFullscreen ? (
                <>
                  <Stars size={28} className={styles.emptyHeroIcon} aria-hidden />
                  <p className={styles.emptyHeroTitle}>Intelligence</p>
                  <p className={styles.emptyHeroBody}>
                    Por ahora puedes crear ventas: elige cliente y productos, y
                    confirma el borrador cuando esté listo.
                  </p>
                  <p className={styles.emptyHeroHint}>
                    Pronto podrás hacer mucho más desde aquí — cotizaciones,
                    inventario y el resto de tu operación. Eso viene en el
                    futuro.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Por ahora solo crear ventas. Pide cliente y productos; el
                    borrador aparecerá para confirmar.
                  </p>
                  <p className={styles.emptyHint}>
                    Más capacidades llegan pronto.
                  </p>
                </>
              )}
            </div>
          )}
          {items.map((item, i) => {
            if (item.kind === 'action') {
              if (item.resolved) return null;
              return renderActionCard(item.action, i, {
                disabled: sending || outOfCredits,
                onConfirm: (action, index) => void handleConfirmAction(action, index),
                onDismiss: handleDismissAction,
              });
            }
            return (
              <div
                key={`${item.role}-${i}`}
                className={`${styles.bubble} ${
                  item.role === 'user' ? styles.bubbleUser : styles.bubbleModel
                }`}
              >
                {item.role === 'model' ? (
                  <ChatMarkdown content={item.content} className={styles.md} />
                ) : (
                  item.content
                )}
                {item.role === 'model' && item.timings ? (
                  <div className={styles.timings}>
                    {formatAgentTimings(item.timings)}
                  </div>
                ) : null}
              </div>
            );
          })}
          {sending && (
            <div className={`${styles.bubble} ${styles.bubbleModel}`}>
              Pensando…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form
          className={styles.composer}
          onSubmit={e => {
            e.preventDefault();
            void handleSend();
          }}
        >
          <textarea
            className={styles.input}
            value={draft}
            placeholder={
              !hasCapacity
                ? 'Activa Intelligence arriba para enviar mensajes'
                : outOfCredits && trialing
                  ? 'Activa un plan arriba para seguir chateando'
                  : outOfCredits
                    ? 'Compra un paquete arriba para seguir chateando'
                    : 'Ej: Venta a HARDWARE Y MAS, 3 portátiles Lenovo…'
            }
            rows={isFullscreen ? 3 : 2}
            disabled={sending || !hasCapacity || outOfCredits}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
          />
          <button
            type="submit"
            className={styles.sendBtn}
            disabled={
              sending || !draft.trim() || !hasCapacity || outOfCredits
            }
            aria-label="Enviar"
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default IntelligenceChatShell;
